import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import client, { clearAuthHeaders } from "@/api/client";

const authAxios = axios.create({
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!user.value);

  async function initialize() {
    const storedUser = localStorage.getItem("auth_user");
    const accessToken = localStorage.getItem("auth_access-token");

    if (storedUser && accessToken) {
      try {
        user.value = JSON.parse(storedUser);
        const response = await client.get("/me");
        user.value = response.data.user;
        localStorage.setItem("auth_user", JSON.stringify(response.data.user));
      } catch (e) {
        clearAuthHeaders();
        user.value = null;
      }
    }
    initialized.value = true;
  }

  async function login(email, password) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.post("/auth/sign_in", { email, password });
      
      const headers = response.headers;
      ["access-token", "client", "uid", "expiry", "token-type"].forEach((h) => {
        if (headers[h]) localStorage.setItem(`auth_${h}`, headers[h]);
      });

      user.value = response.data.data;
      localStorage.setItem("auth_user", JSON.stringify(response.data.data));
      return { success: true };
    } catch (e) {
      error.value = e.response?.data?.errors?.[0] || "Erro ao fazer login";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function register(data) {
    loading.value = true;
    error.value = null;
    try {
      const response = await authAxios.post("/auth", {
        name: data.name,
        email: data.email,
        password: data.password,
        password_confirmation: data.passwordConfirmation,
      });

      const headers = response.headers;
      ["access-token", "client", "uid", "expiry", "token-type"].forEach((h) => {
        if (headers[h]) localStorage.setItem(`auth_${h}`, headers[h]);
      });

      user.value = response.data.data;
      localStorage.setItem("auth_user", JSON.stringify(response.data.data));
      return { success: true };
    } catch (e) {
      error.value =
        e.response?.data?.errors?.full_messages?.[0] ||
        e.response?.data?.errors?.[0] ||
        "Erro ao criar conta";
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      await authAxios.delete("/auth/sign_out", {
        headers: {
          "access-token": localStorage.getItem("auth_access-token"),
          client: localStorage.getItem("auth_client"),
          uid: localStorage.getItem("auth_uid"),
        },
      });
    } catch (e) {
      // Ignore errors on logout
    } finally {
      clearAuthHeaders();
      user.value = null;
    }
  }

  return {
    user,
    loading,
    error,
    initialized,
    isAuthenticated,
    initialize,
    login,
    register,
    logout,
  };
});
